import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';

/**
 * Modern Select Component
 * Beautiful dropdown with smooth animations and gradient accents
 */

export default function Select({
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  leftIcon: LeftIcon,
  className = '',
  disabled = false,
  error = false,
  ...props
}) {
  const hasLeftIcon = LeftIcon !== undefined;

  return (
    <div className="relative group">
      {/* Left Icon */}
      {LeftIcon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
          <LeftIcon className="w-5 h-5 text-slate-400 group-hover:text-slate-500 transition-colors" aria-hidden="true" />
        </div>
      )}

      {/* Select Element */}
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={clsx(
          // Base styles
          'w-full py-3 text-sm font-medium rounded-xl transition-all duration-200',
          'bg-white border-2 border-slate-200',
          'appearance-none cursor-pointer',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
          'disabled:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60',
          
          // Hover state
          'hover:border-slate-300 hover:shadow-sm',
          
          // Error state
          error && 'border-danger-500 focus:ring-danger-500',
          
          // Padding adjustments for icons
          hasLeftIcon ? 'pl-10 pr-10' : 'px-4 pr-10',
          
          className
        )}
        aria-invalid={error ? 'true' : 'false'}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Chevron Icon */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <ChevronDown 
          className={clsx(
            'w-5 h-5 transition-all duration-200',
            disabled ? 'text-slate-300' : 'text-slate-400 group-hover:text-slate-600'
          )} 
          aria-hidden="true" 
        />
      </div>

      {/* Gradient Border Effect on Focus */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 opacity-0 group-focus-within:opacity-100 -z-10 blur-sm transition-opacity duration-200" />
    </div>
  );
}

/**
 * SelectField Component
 * Select with label and error message
 */
export function SelectField({
  label,
  error,
  helperText,
  required = false,
  className = '',
  ...selectProps
}) {
  return (
    <div className={clsx('space-y-2', className)}>
      {label && (
        <label className="block text-sm font-medium text-slate-700">
          {label}
          {required && <span className="text-danger-500 ml-1" aria-label="required">*</span>}
        </label>
      )}

      <Select error={error} {...selectProps} />

      {error && (
        <p className="text-xs text-danger-600" role="alert">
          {error}
        </p>
      )}

      {!error && helperText && (
        <p className="text-xs text-slate-500">{helperText}</p>
      )}
    </div>
  );
}